import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase, isPreviewMode } from '../services/supabase';
import { logger } from '../services/logger';
import { isPublicRoute } from '../constants/routes';
import type { UserProgress, UserPreferences } from '../types';

const DEFAULT_PREFERENCES: UserPreferences = {
  notifications: {
    email: true,
    push: true,
    dailyReminders: true,
    weeklyDigest: true
  },
  privacy: {
    profileVisibility: 'public',
    shareProgress: true,
    allowAiAnalysis: true
  },
  ai: {
    enhancedAnalysis: true,
    autoSuggestions: true,
    personalizedCoaching: true
  }
};

export function useUserProgress() {
  const [progress, setProgress] = useState<UserProgress | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const loadProgress = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      if (isPreviewMode()) {
        setProgress({
          id: 'preview-user',
          user_id: 'preview-user',
          lastVisitedRoute: '/',
          lastVisionBoardState: null,
          completedTutorials: [],
          xp: 0,
          level: 1,
          streakDays: 0,
          preferences: DEFAULT_PREFERENCES,
          created_at: new Date(),
          updated_at: new Date()
        });
        setLoading(false);
        return;
      }

      // Directly await the full query chain
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      // If no session and not on public route, redirect to login
      if (!session || sessionError) {
        const currentPath = window.location.pathname;
        if (!isPublicRoute(currentPath)) {
          navigate('/login');
        }
        setLoading(false);
        return;
      }

      // First try to get existing progress - directly await the full query chain
      const { data: existingProgress, error: selectError } = await supabase
        .from('user_progress')
        .select('*')
        .eq('user_id', session.user.id)
        .maybeSingle();

      // If no progress exists, create it
      if (!existingProgress && (!selectError || selectError.code === 'PGRST116')) {
        // Directly await the full query chain
        const { data: newProgress, error: insertError } = await supabase
          .from('user_progress')
          .insert({
            user_id: session.user.id,
            last_visited_route: '/',
            preferences: DEFAULT_PREFERENCES,
            xp: 0,
            level: 1,
            streak_days: 0
          })
          .select()
          .single();

        if (insertError) throw insertError;
        
        if (newProgress) {
          setProgress({
            id: newProgress.id,
            user_id: newProgress.user_id,
            lastVisitedRoute: newProgress.last_visited_route || '/',
            lastVisionBoardState: newProgress.last_vision_board_state,
            completedTutorials: newProgress.completed_tutorials || [],
            xp: newProgress.xp || 0,
            level: newProgress.level || 1,
            streakDays: newProgress.streak_days || 0,
            preferences: newProgress.preferences || DEFAULT_PREFERENCES,
            created_at: new Date(newProgress.created_at),
            updated_at: new Date(newProgress.updated_at)
          });
        }
      } else if (existingProgress) {
        setProgress({
          id: existingProgress.id,
          user_id: existingProgress.user_id,
          lastVisitedRoute: existingProgress.last_visited_route || '/',
          lastVisionBoardState: existingProgress.last_vision_board_state,
          completedTutorials: existingProgress.completed_tutorials || [],
          xp: existingProgress.xp || 0,
          level: existingProgress.level || 1,
          streakDays: existingProgress.streak_days || 0,
          preferences: existingProgress.preferences || DEFAULT_PREFERENCES,
          created_at: new Date(existingProgress.created_at),
          updated_at: new Date(existingProgress.updated_at)
        });
      }
    } catch (err: any) {
      logger.error('Error loading user progress:', err);
      setError('Failed to load user progress');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadProgress();
  }, [loadProgress]);

  const updateProgress = useCallback(async (updates: Partial<UserProgress>) => {
    if (!progress?.user_id || isPreviewMode()) return;

    try {
      setError(null);

      const dbUpdates: Record<string, any> = {
        updated_at: new Date().toISOString()
      };

      if ('lastVisitedRoute' in updates) {
        dbUpdates.last_visited_route = updates.lastVisitedRoute;
      }
      if ('lastVisionBoardState' in updates) {
        dbUpdates.last_vision_board_state = updates.lastVisionBoardState;
      }
      if ('completedTutorials' in updates) {
        dbUpdates.completed_tutorials = updates.completedTutorials;
      }
      if ('preferences' in updates) {
        dbUpdates.preferences = updates.preferences;
      }
      if ('xp' in updates) {
        dbUpdates.xp = updates.xp;
      }
      if ('level' in updates) {
        dbUpdates.level = updates.level;
      }
      if ('streakDays' in updates) {
        dbUpdates.streak_days = updates.streakDays;
      }

      // Directly await the full query chain
      const { error: updateError } = await supabase
        .from('user_progress')
        .update(dbUpdates)
        .eq('user_id', progress.user_id);

      if (updateError) throw updateError;

      setProgress(prev => prev ? { ...prev, ...updates } : null);
    } catch (err: any) {
      logger.error('Error updating progress:', err);
      setError('Failed to update progress');
    }
  }, [progress?.user_id]);

  return {
    progress,
    loading,
    error,
    updateProgress,
    refresh: loadProgress
  };
}